import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Calculator, Info, Sparkle, TrendUp, ShieldCheck } from "@phosphor-icons/react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Layout } from "../../components/layout/Layout";
import { formatCurrency, API_URL } from "../../lib/utils";
import { LiquidGlassBg } from "../../components/ui/LiquidGlassComponents";

const models = [
  { value: "no_healthcare", label: "No Formal Healthcare" },
  { value: "reimbursement", label: "Reimbursement Based" },
  { value: "insurance", label: "Insurance / Takaful" },
  { value: "employer_pool", label: "Employer Funded Pool" },
  { value: "hybrid", label: "Hybrid Model" },
];

const industries = [
  { value: "technology", label: "Technology / IT" },
  { value: "manufacturing", label: "Manufacturing" },
  { value: "healthcare", label: "Healthcare" },
  { value: "finance", label: "Finance / Banking" },
  { value: "retail", label: "Retail" },
  { value: "education", label: "Education" },
  { value: "other", label: "Other" },
];

export default function QuickCalculator() {
  const [formData, setFormData] = useState({
    number_of_employees: "",
    current_annual_spend: "",
    current_model: "",
    industry: "",
    admin_hours_weekly: "",
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await fetch(`${API_URL}/calculators/quick`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          number_of_employees: parseInt(formData.number_of_employees),
          current_annual_spend: parseFloat(formData.current_annual_spend) || 0,
          current_model: formData.current_model,
          industry: formData.industry || null,
          admin_hours_weekly: parseFloat(formData.admin_hours_weekly) || null,
        }),
      });

      if (!response.ok) throw new Error("Calculation failed");
      const data = await response.json();
      setResult(data);
    } catch (err) {
      setError("Failed to calculate. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <section className="bg-[#030712] min-h-screen relative overflow-hidden pt-32 pb-20">
        <LiquidGlassBg />
        
        {/* Decorative Elements */}
        <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[#0048ff]/30 to-transparent" />
        <div className="absolute top-1/4 -left-1/4 w-1/2 h-1/2 bg-[#0048ff]/10 blur-[120px] rounded-full pointer-events-none" />

        <div className="container-wide relative z-10">
          <div className="max-w-6xl mx-auto">
            {/* Header */}
            <motion.div
              className="text-center mb-16"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-[#0048ff] text-sm font-medium mb-6 backdrop-blur-md">
                <Calculator size={18} weight="bold" />
                <span>Financial Infrastructure Analysis</span>
              </div>
              <h1 className="text-[40px] md:text-[56px] font-bold text-white mb-6 tracking-tighter leading-tight">
                Quick <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0048ff] to-cyan-400">Savings</span> Estimator
              </h1>
              <p className="text-white/40 text-lg max-w-2xl mx-auto leading-relaxed">
                Analyze your current healthcare spend and discover the potential for high-governance optimization within minutes.
              </p>
            </motion.div>

            <div className="grid lg:grid-cols-12 gap-8 items-start">
              {/* Form Section */}
              <motion.div
                className="lg:col-span-7"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
              >
                <div className="liquid-glass bg-white/[0.03] backdrop-blur-2xl border border-white/10 rounded-[32px] overflow-hidden p-8 md:p-10">
                  <div className="flex items-center gap-4 mb-10">
                    <div className="w-12 h-12 rounded-2xl bg-[#0048ff]/20 flex items-center justify-center text-[#0048ff] border border-[#0048ff]/30">
                      <Sparkle size={24} weight="duotone" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-white">Input Parameters</h2>
                      <p className="text-white/40 text-sm">Configure your organizational profile</p>
                    </div>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-8">
                    <div className="grid md:grid-cols-2 gap-8">
                      <div className="space-y-3">
                        <Label className="text-white/60 text-sm font-medium ml-1">Number of Employees *</Label>
                        <Input
                          type="number"
                          placeholder="e.g., 250"
                          value={formData.number_of_employees}
                          onChange={(e) => setFormData({ ...formData, number_of_employees: e.target.value })}
                          className="bg-white/5 border-white/10 text-white h-14 rounded-2xl focus:border-[#0048ff] focus:ring-[#0048ff]/20 transition-all placeholder:text-white/20"
                          required
                        />
                      </div>

                      <div className="space-y-3">
                        <Label className="text-white/60 text-sm font-medium ml-1">Industry (Optional)</Label>
                        <Select
                          value={formData.industry}
                          onValueChange={(value) => setFormData({ ...formData, industry: value })}
                        >
                          <SelectTrigger className="bg-white/5 border-white/10 text-white h-14 rounded-2xl focus:border-[#0048ff] transition-all">
                            <SelectValue placeholder="Select industry" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#0f111a] border-white/10 text-white">
                            {industries.map((ind) => (
                              <SelectItem key={ind.value} value={ind.value} className="focus:bg-[#0048ff] focus:text-white cursor-pointer">
                                {ind.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <Label className="text-white/60 text-sm font-medium ml-1">Current Healthcare Model *</Label>
                      <Select
                        value={formData.current_model}
                        onValueChange={(value) => setFormData({ ...formData, current_model: value })}
                      >
                        <SelectTrigger className="bg-white/5 border-white/10 text-white h-14 rounded-2xl focus:border-[#0048ff] transition-all">
                          <SelectValue placeholder="Select current model" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0f111a] border-white/10 text-white">
                          {models.map((model) => (
                            <SelectItem key={model.value} value={model.value} className="focus:bg-[#0048ff] focus:text-white cursor-pointer">
                              {model.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8">
                      <div className="space-y-3">
                        <Label className="text-white/60 text-sm font-medium ml-1">
                          Current Annual Spend (PKR)
                          {formData.current_model !== "no_healthcare" && " *"}
                        </Label>
                        <Input
                          type="number"
                          placeholder="e.g., 5,000,000"
                          value={formData.current_annual_spend}
                          onChange={(e) => setFormData({ ...formData, current_annual_spend: e.target.value })}
                          className="bg-white/5 border-white/10 text-white h-14 rounded-2xl focus:border-[#0048ff] transition-all placeholder:text-white/20"
                          required={formData.current_model !== "no_healthcare"}
                        />
                      </div>

                      {formData.current_model === "reimbursement" && (
                        <div className="space-y-3">
                          <Label className="text-white/60 text-sm font-medium ml-1">Admin Hours Weekly</Label>
                          <Input
                            type="number"
                            placeholder="e.g., 10"
                            value={formData.admin_hours_weekly}
                            onChange={(e) => setFormData({ ...formData, admin_hours_weekly: e.target.value })}
                            className="bg-white/5 border-white/10 text-white h-14 rounded-2xl focus:border-[#0048ff] transition-all placeholder:text-white/20"
                          />
                        </div>
                      )}
                    </div>

                    {error && (
                      <motion.div 
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        className="p-4 bg-red-500/10 border border-red-500/20 rounded-2xl text-red-400 text-sm"
                      >
                        {error}
                      </motion.div>
                    )}

                    <Button
                      type="submit"
                      className="w-full h-14 bg-[#0048ff] hover:bg-[#003bce] text-white font-bold rounded-2xl transition-all shadow-lg shadow-[#0048ff]/20 disabled:opacity-50 mt-4 group"
                      disabled={loading || !formData.current_model}
                    >
                      {loading ? (
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          <span>Executing Analysis...</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <span>Verify Optimization Potential</span>
                          <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                        </div>
                      )}
                    </Button>
                  </form>
                </div>
              </motion.div>

              {/* Results Section */}
              <motion.div
                className="lg:col-span-5"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
              >
                <AnimatePresence mode="wait">
                  {result ? (
                    <motion.div
                      key="result"
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      className="liquid-glass bg-[#0048ff]/5 border border-[#0048ff]/20 rounded-[32px] p-8 md:p-10 relative overflow-hidden"
                    >
                      {/* Glow inside result */}
                      <div className="absolute -top-20 -right-20 w-40 h-40 bg-[#0048ff]/20 blur-[60px] rounded-full" />
                      
                      <div className="flex items-center gap-4 mb-8">
                        <div className="w-12 h-12 rounded-2xl bg-[#0048ff] flex items-center justify-center text-white shadow-lg shadow-[#0048ff]/40">
                          <TrendUp size={24} weight="bold" />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-white leading-none">Your Estimate</h3>
                          <p className="text-white/40 text-sm mt-1">Optimization Projection</p>
                        </div>
                      </div>

                      <div className="space-y-8">
                        {result.savings.type === "employee_discount_value" && (
                          <div className="space-y-4">
                            <div>
                              <p className="text-white/40 text-sm mb-2 uppercase tracking-widest font-semibold">Value Generated</p>
                              <div className="text-4xl font-bold text-white tracking-tighter">
                                {formatCurrency(result.savings.low)}
                                <span className="text-lg text-white/40 mx-2">—</span>
                                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0048ff] to-cyan-400">
                                  {formatCurrency(result.savings.high)}
                                </span>
                              </div>
                            </div>
                            <div className="p-4 bg-white/5 rounded-2xl border border-white/10">
                              <p className="text-white/70 text-sm leading-relaxed">{result.savings.description}</p>
                            </div>
                          </div>
                        )}

                        {result.savings.type === "reimbursement_optimization" && (
                          <div className="space-y-6">
                            <div className="grid grid-cols-2 gap-4">
                              <div className="p-5 bg-white/5 rounded-2xl border border-white/5">
                                <p className="text-white/40 text-[10px] uppercase font-bold tracking-wider mb-2">Leakage Reduction</p>
                                <p className="text-lg font-bold text-emerald-400">{formatCurrency(result.savings.leakage_reduction.low)}+</p>
                              </div>
                              <div className="p-5 bg-white/5 rounded-2xl border border-white/5">
                                <p className="text-white/40 text-[10px] uppercase font-bold tracking-wider mb-2">Admin Gain</p>
                                <p className="text-lg font-bold text-cyan-400">{formatCurrency(result.savings.admin_efficiency.low)}+</p>
                              </div>
                            </div>
                            <div className="pt-6 border-t border-white/10">
                              <p className="text-white/40 text-sm mb-2 uppercase tracking-widest font-semibold">Total Savings</p>
                              <div className="text-4xl font-bold text-white tracking-tighter">
                                {formatCurrency(result.savings.total.low)}
                                <span className="text-lg text-white/40 mx-2">—</span>
                                <span className="text-[#0048ff]">{formatCurrency(result.savings.total.high)}</span>
                              </div>
                            </div>
                          </div>
                        )}

                        {(result.savings.type === "premium_optimization" ||
                          result.savings.type === "pool_efficiency" ||
                          result.savings.type === "hybrid_optimization") && (
                          <div className="space-y-6">
                            <div>
                              <p className="text-white/40 text-sm mb-2 uppercase tracking-widest font-semibold">Annual Savings</p>
                              <div className="text-4xl font-bold text-white tracking-tighter">
                                {formatCurrency(result.savings.low)}
                                <span className="text-lg text-white/40 mx-2">—</span>
                                <span className="text-[#0048ff]">{formatCurrency(result.savings.high)}</span>
                              </div>
                            </div>
                            <div className="p-5 bg-white/5 rounded-2xl border border-white/10">
                              <p className="text-white/70 text-sm leading-relaxed">{result.savings.description}</p>
                              {result.savings.liquidity_benefit && (
                                <div className="mt-3 flex items-center gap-2 text-emerald-400 text-xs font-bold">
                                  <ShieldCheck size={16} />
                                  <span>{result.savings.liquidity_benefit}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        <div className="pt-8 border-t border-white/10">
                          <div className="flex flex-col gap-6">
                            <div>
                              <p className="text-white/40 text-sm mb-3 font-medium">Recommended Infrastructure</p>
                              <div className="px-4 py-3 bg-[#0048ff] text-white font-bold rounded-xl inline-block shadow-lg shadow-[#0048ff]/20">
                                {result.recommendation}
                              </div>
                            </div>
                            
                            <div className="grid gap-3">
                              <Link to="/calculators/advanced">
                                <Button className="w-full bg-white text-[#11111f] hover:bg-white/90 font-bold h-12 rounded-xl transition-all">
                                  Access Detailed Analysis
                                </Button>
                              </Link>
                              <Link to="/demo">
                                <Button variant="outline" className="w-full border-white/10 text-white hover:bg-white/5 h-12 rounded-xl transition-all">
                                  Review with Architect
                                </Button>
                              </Link>
                            </div>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ) : (
                    <motion.div
                      key="placeholder"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="liquid-glass bg-white/[0.02] border border-white/10 rounded-[32px] p-12 text-center h-[500px] flex flex-col items-center justify-center space-y-6"
                    >
                      <div className="w-20 h-20 rounded-full bg-white/5 flex items-center justify-center mb-4 relative">
                        <Info size={40} className="text-white/10" weight="light" />
                        <div className="absolute inset-0 rounded-full border border-white/10 animate-pulse" />
                      </div>
                      <h3 className="text-xl font-bold text-white/60">Ready to Analyze</h3>
                      <p className="text-white/20 text-sm max-w-[240px] leading-relaxed">
                        Input your company metrics to unlock high-governance savings projections and recommended rail configurations.
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
